import { jsx as _jsx } from "react/jsx-runtime";
/**
 * AuthProvider — shared authentication context for all ecosystem products.
 *
 * Wraps AWS Amplify v6 auth and provides a React context with:
 *   - Authentication state (isAuthed, isAuthReady, user, tokens)
 *   - Login / logout / refresh methods
 *   - Cross-tab sign-out sync via BroadcastChannel
 *   - Periodic token refresh (50 min interval)
 *
 * Products configure auth once via configureAuth(), then wrap their app:
 *
 * @example
 * ```tsx
 * import { configureAuth, AuthProvider, useAuth } from "@d-m-organization/app-shell/auth";
 *
 * configureAuth({ ... });
 *
 * function App() {
 *   return (
 *     <AuthProvider onSignOut={() => clearMyProductCache()}>
 *       <MyRoutes />
 *     </AuthProvider>
 *   );
 * }
 * ```
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { fetchAuthSession, getCurrentUser, signInWithRedirect, signOut, } from "aws-amplify/auth";
import { Hub } from "aws-amplify/utils";
import { getAuthConfig } from "./configure.js";
import { refreshAndGetToken, registerTokenRefreshedCallback, } from "./sessionRefresher.js";
import { parseJwtClaims } from "./jwt.js";
// =============================================================================
// Constants
// =============================================================================
const DEFAULT_CHANNEL_NAME = "ecosystem-auth-sync";
const REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 minutes
// =============================================================================
// Context
// =============================================================================
const AuthContext = createContext(undefined);
// =============================================================================
// Helpers
// =============================================================================
function getInitials(value) {
    const trimmed = value.trim();
    if (!trimmed)
        return "U";
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1)
        return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
}
function getUserLabel(user) {
    if (!user)
        return "U";
    if (user.name)
        return getInitials(user.name);
    if (user.email) {
        const prefix = user.email.split("@")[0] ?? "";
        const normalized = prefix.replace(/[._-]+/g, " ");
        return getInitials(normalized);
    }
    return "U";
}
function extractUserFromClaims(claims, userId) {
    const email = typeof claims?.email === "string" ? claims.email : undefined;
    const givenName = typeof claims?.given_name === "string" ? claims.given_name : "";
    const familyName = typeof claims?.family_name === "string" ? claims.family_name : "";
    const fullName = (typeof claims?.name === "string" ? claims.name.trim() : "") ||
        [givenName, familyName].filter(Boolean).join(" ").trim() ||
        undefined;
    const ecosystemId = typeof claims?.["custom:ecosystem_id"] === "string"
        ? claims["custom:ecosystem_id"]
        : undefined;
    const groups = Array.isArray(claims?.["cognito:groups"])
        ? claims["cognito:groups"]
        : undefined;
    // AL-M3: fail-safe — only the exact string 'true' counts as having a
    // password. Missing/'false'/malformed → false → forced-setup gate fires.
    const hasPassword = claims?.["custom:has_password"] === "true";
    return { id: userId, email, name: fullName, ecosystemId, groups, hasPassword };
}
export function AuthProvider({ children, onSignOut }) {
    const [user, setUser] = useState(null);
    const [idToken, setIdToken] = useState(null);
    const [accessToken, setAccessToken] = useState(null);
    const [isAuthed, setIsAuthed] = useState(false);
    const [isAuthReady, setIsAuthReady] = useState(false);
    const channelRef = useRef(null);
    const onSignOutRef = useRef(onSignOut);
    onSignOutRef.current = onSignOut;
    // ─── BroadcastChannel for cross-tab sync ────────────────────────────────
    useEffect(() => {
        if (typeof BroadcastChannel === "undefined")
            return;
        let channelName = DEFAULT_CHANNEL_NAME;
        try {
            channelName = getAuthConfig().broadcastChannelName ?? DEFAULT_CHANNEL_NAME;
        }
        catch {
            // configureAuth not called yet — use default
        }
        const channel = new BroadcastChannel(channelName);
        channelRef.current = channel;
        channel.onmessage = (event) => {
            if (event.data?.type === "signed-out") {
                setUser(null);
                setIdToken(null);
                setAccessToken(null);
                setIsAuthed(false);
                setIsAuthReady(true);
                onSignOutRef.current?.();
            }
        };
        return () => {
            channel.close();
            channelRef.current = null;
        };
    }, []);
    // ─── Core state management ──────────────────────────────────────────────
    const clearAuth = useCallback(() => {
        setUser(null);
        setIdToken(null);
        setAccessToken(null);
        setIsAuthed(false);
        onSignOutRef.current?.();
    }, []);
    const refreshSession = useCallback(async () => {
        try {
            const session = await fetchAuthSession();
            const nextIdToken = session.tokens?.idToken?.toString() ?? null;
            const nextAccessToken = session.tokens?.accessToken?.toString() ?? null;
            if (!nextIdToken) {
                clearAuth();
                return;
            }
            const currentUser = await getCurrentUser();
            const claims = parseJwtClaims(nextIdToken);
            const userId = currentUser.userId ?? currentUser.username;
            const authUser = extractUserFromClaims(claims, userId);
            setUser(authUser);
            setIdToken(nextIdToken);
            setAccessToken(nextAccessToken);
            setIsAuthed(true);
        }
        catch {
            clearAuth();
        }
        finally {
            setIsAuthReady(true);
        }
    }, [clearAuth]);
    // ─── Initial session check ──────────────────────────────────────────────
    useEffect(() => {
        void refreshSession();
    }, [refreshSession]);
    // ─── Amplify Hub events ─────────────────────────────────────────────────
    useEffect(() => {
        const unsubscribe = Hub.listen("auth", ({ payload }) => {
            switch (payload.event) {
                case "tokenRefresh":
                    void refreshSession();
                    break;
                case "tokenRefresh_failure":
                    clearAuth();
                    setIsAuthReady(true);
                    break;
                case "signedOut":
                    clearAuth();
                    setIsAuthReady(true);
                    break;
                case "signInWithRedirect":
                    void refreshSession();
                    break;
            }
        });
        return unsubscribe;
    }, [refreshSession, clearAuth]);
    // ─── Periodic token refresh (50 min) ────────────────────────────────────
    useEffect(() => {
        if (!isAuthed)
            return;
        const timer = setInterval(async () => {
            try {
                await refreshAndGetToken();
            }
            catch {
                // Hub tokenRefresh_failure handles this
            }
        }, REFRESH_INTERVAL_MS);
        return () => clearInterval(timer);
    }, [isAuthed]);
    // ─── Session refresher callback (for API client 401 retry) ─────────────
    useEffect(() => {
        const prev = registerTokenRefreshedCallback(() => {
            void refreshSession();
        });
        return () => {
            registerTokenRefreshedCallback(prev);
        };
    }, [refreshSession]);
    // ─── Auth actions ───────────────────────────────────────────────────────
    const login = useCallback(async () => {
        await signInWithRedirect();
    }, []);
    const loginWith = useCallback(async (provider) => {
        await signInWithRedirect({ provider });
    }, []);
    const logout = useCallback(async () => {
        try {
            await signOut({ global: true });
        }
        finally {
            clearAuth();
            channelRef.current?.postMessage({
                type: "signed-out",
            });
        }
    }, [clearAuth]);
    // ─── Context value ─────────────────────────────────────────────────────
    const userLabel = useMemo(() => getUserLabel(user), [user]);
    const value = useMemo(() => ({
        isAuthed,
        isAuthReady,
        user,
        userLabel,
        idToken,
        accessToken,
        login,
        loginWith,
        logout,
        refreshSession,
    }), [
        isAuthed,
        isAuthReady,
        user,
        userLabel,
        idToken,
        accessToken,
        login,
        loginWith,
        logout,
        refreshSession,
    ]);
    return _jsx(AuthContext.Provider, { value: value, children: children });
}
// =============================================================================
// Hook
// =============================================================================
/**
 * Access the auth context. Must be used within an AuthProvider.
 *
 * @example
 * ```tsx
 * function Dashboard() {
 *   const { isAuthed, user, logout } = useAuth();
 *   if (!isAuthed) return <LoginPage />;
 *   return <div>Hello {user?.name}</div>;
 * }
 * ```
 */
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
